import { useTranslation } from "react-i18next"; // Hook for internationalization
import { useEffect, useState } from "react";
import { GetFirstSystemConfig, PartialUpdateSystemConfig } from "../../../../reduxToolkit/SystemConfig/SystemConfigApi";
import { useDispatch, useSelector } from "react-redux";
import { SETTING_CONFIG_MODEL } from "../Model/SettingConfigModel";
import HandleChange from "../../../../Helpers/HandleChange";
import { toast } from "react-toastify";
import {permissionObj} from "../../../../Helpers/permission"; // Library for showing notifications

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 * SettingConfigApp
 *
 * A custom hook responsible for fetching, updating, and managing system configuration settings.
 * It integrates with Redux to dispatch actions and access global state.
 *
 * @returns {
 *   t: Function,
 *   DEV: boolean,
 *   permission: any,
 *   disabled: boolean,
 *   settingConfig: object,
 *   emptyFields: boolean,
 *   setSettingConfig: Function,
 *   handleUpdate: Function,
 *   handleChange: Function
 * }
 */
const SettingConfigApp = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(); // Initialize translation function
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development mode flag from Redux
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions from Redux

    const [settingConfig, setSettingConfig] = useState({ ...SETTING_CONFIG_MODEL }); // Local state for the config form
    const [emptyFields, setEmptyFields] = useState(false); // Tracks if any fields are left empty
    const [disabled, setDisabled] = useState(false); // Tracks if any fields are left empty
    const { handleChange } = HandleChange({ model: settingConfig, setModel: setSettingConfig }); // Custom change handler for form fields

    // Fetch the initial system configuration when the component mounts
    useEffect(() => {
        setDisabled(!DEV && !permission.includes(permissionObj.WEB_SYSTEM_CONFIG_UPDATE));
    }, [permission]);

    useEffect(() => {
        dispatch(GetFirstSystemConfig()).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setSettingConfig(SETTING_CONFIG_MODEL.set(data)); // Populate form with fetched config
            }
        });
    }, []);

    // Handles updating the system configuration
    const handleUpdate = () => {
       dispatch(PartialUpdateSystemConfig(settingConfig.toRegister())).then(({payload:{data:{success}}})=> {
           if(success) toast.success(t("has_been_updated_successfully"));
           else toast.error(t("request_error"));
       });
    }

    // Return state and handlers to be used in the component
    return {
        t,
        DEV,
        permission,
        disabled,
        settingConfig,
        emptyFields,
        setSettingConfig,
        handleUpdate,
        handleChange
    };
}

export default SettingConfigApp;
